import { z } from "zod";
/**
 * Dashboards: request shapes and the response types both sides agree on.
 *
 * A dashboard stores configuration (widgets, default filters, who may view),
 * never figures. Each widget's numbers are computed by the server for whoever
 * is looking, so the types below are split in two: what is *stored*
 * (`WidgetConfig`) and what a widget *returns* (`WidgetData`).
 */
export const WIDGET_TYPES = [
    "number_tile",
    "tasks_by",
    "completion_trend",
    "workload",
    "time_logged",
    "budget_burn",
    "goals_progress",
    "project_health",
    "task_list",
];
/** Columns a widget spans in the grid: small, medium, large. */
export const WIDGET_SIZES = ["S", "M", "L"];
export const MAX_WIDGETS = 24;
export const MAX_FILTER_PROJECTS = 20;
/** The longest date range a widget will aggregate over. */
export const MAX_RANGE_DAYS = 366;
const day = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), "Invalid date.");
/**
 * The global filter bar. Every key is optional: absent means "any".
 * `from`/`to` are calendar dates in the viewer's time zone, inclusive.
 * (The office is not a filter: a dashboard belongs to one office, the active one.)
 */
export const dashboardFilters = z
    .object({
    teamId: z.string().uuid(),
    projectIds: z.array(z.string().uuid()).max(MAX_FILTER_PROJECTS),
    from: day,
    to: day,
    assigneeId: z.string().uuid(),
})
    .partial()
    .strict()
    .refine((f) => !f.from || !f.to || f.from <= f.to, { message: "The range ends before it starts.", path: ["to"] })
    .refine((f) => !f.from || !f.to || Date.parse(f.to) - Date.parse(f.from) <= MAX_RANGE_DAYS * 86_400_000, {
    message: `The range is longer than ${MAX_RANGE_DAYS} days.`,
    path: ["to"],
});
/* ───────────────────────────── widget configs ───────────────────────────── */
export const TASKS_BY_GROUPS = ["status", "assignee", "project", "priority", "custom_field"];
export const NUMBER_TILE_METRICS = ["open", "overdue", "completed"];
const numberTile = z.object({ metric: z.enum(NUMBER_TILE_METRICS).default("open") }).strict();
const tasksBy = z
    .object({
    groupBy: z.enum(TASKS_BY_GROUPS).default("status"),
    /** The dropdown custom field to group by; required for `custom_field`. */
    fieldId: z.string().min(1).max(64).optional(),
    chart: z.enum(["bar", "donut"]).default("bar"),
})
    .strict()
    .refine((c) => c.groupBy !== "custom_field" || !!c.fieldId, { message: "Pick a dropdown field.", path: ["fieldId"] });
const empty = z.object({}).strict();
const workload = z
    .object({
    weeks: z.number().int().min(1).max(8).default(4),
    limit: z.number().int().min(1).max(25).default(10),
})
    .strict();
const timeLogged = z.object({ groupBy: z.enum(["person", "project"]).default("person") }).strict();
const limited = (max, fallback) => z.object({ limit: z.number().int().min(1).max(max).default(fallback) }).strict();
const goalsProgress = z
    .object({ periodLabel: z.string().trim().min(1).max(40).optional(), limit: z.number().int().min(1).max(25).default(10) })
    .strict();
/** A saved-view-like filter for the task list; the same words as the Work filter bar. */
export const taskListFilters = z
    .object({
    status: z.string().max(100),
    priority: z.string().max(100),
    mine: z.boolean(),
    overdue: z.boolean(),
    q: z.string().max(200),
})
    .partial()
    .strict();
const taskList = z
    .object({ filters: taskListFilters.default({}), limit: z.number().int().min(1).max(25).default(10) })
    .strict();
export const widgetConfigSchemas = {
    number_tile: numberTile,
    tasks_by: tasksBy,
    completion_trend: empty,
    workload,
    time_logged: timeLogged,
    budget_burn: limited(25, 10),
    goals_progress: goalsProgress,
    project_health: limited(50, 10),
    task_list: taskList,
};
const title = z.string().trim().min(1).max(80);
export const createWidgetBody = z
    .object({
    type: z.enum(WIDGET_TYPES),
    title: title.nullish(),
    size: z.enum(WIDGET_SIZES).optional(),
    config: z.record(z.string(), z.unknown()).default({}),
})
    .strict();
export const patchWidgetBody = z
    .object({
    title: title.nullable().optional(),
    size: z.enum(WIDGET_SIZES).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const reorderWidgetsBody = z.object({ widgetIds: z.array(z.string().uuid()).min(1).max(MAX_WIDGETS) }).strict();
/** The size a widget starts at when none is given. */
export const DEFAULT_WIDGET_SIZE = {
    number_tile: "S",
    tasks_by: "M",
    completion_trend: "L",
    workload: "M",
    time_logged: "M",
    budget_burn: "M",
    goals_progress: "M",
    project_health: "L",
    task_list: "L",
};
/* ───────────────────────────── dashboards ───────────────────────────── */
const name = z.string().trim().min(1).max(100);
export const dashboardSharing = z
    .object({
    office: z.boolean().default(false),
    teamIds: z.array(z.string().uuid()).max(30).default([]),
    userIds: z.array(z.string().uuid()).max(100).default([]),
})
    .strict();
export const createDashboardBody = z
    .object({
    name,
    description: z.string().trim().max(500).nullish(),
    filters: dashboardFilters.default({}),
    sharing: dashboardSharing.optional(),
})
    .strict();
export const patchDashboardBody = z
    .object({
    name: name.optional(),
    description: z.string().trim().max(500).nullable().optional(),
    filters: dashboardFilters.optional(),
    sharing: dashboardSharing.optional(),
})
    .strict();
//# sourceMappingURL=dashboards.js.map